export const ROOT_SELECTORS_BY_PRIORITY = ['dialog[open]', 'body > main#main'];
export const DEFAULT_Z_INDEX = 10000;

const DATA_ATTRIBUTE_STICKY_CONTENT = 'data-catalog-sticky-content';

export const WITH_DATA_ATTRIBUTE_STICKY_CONTENT = {
  [DATA_ATTRIBUTE_STICKY_CONTENT]: true
};

/**
 * Detects if there's a fixed-position header at the top of the page
 * and returns its height. This is useful for calculating scroll offsets
 * or intersection observer margins when you need to account for fixed headers.
 *
 * @returns {number | null} The height of the fixed header in pixels if found, otherwise null
 */
export function getFixedHeaderHeight(): number | null {
  const { height, top } =
    getFixedHeader()?.getBoundingClientRect?.() ?? ({} as DOMRect);
  return height == null ? null : height + top;
}

/**
 * Gets the z-index of the fixed header element at the top of the page.
 * This is useful for ensuring other fixed elements (like sticky purchase sections)
 * appear correctly in relation to the header.
 *
 * @returns {number} The z-index of the fixed header, or the fallback
 */
export function getFixedHeaderZIndex(): number {
  const header = getFixedHeader();

  if (!header) {
    return DEFAULT_Z_INDEX;
  }

  const zIndex = window.getComputedStyle(header)?.zIndex;
  const numericZIndex = parseInt(zIndex, 10);

  return Number.isNaN(numericZIndex) ? DEFAULT_Z_INDEX : numericZIndex;
}

/**
 * Detects the fixed-position element at the top of the page (1,1 position).
 * Works for any element type (div, header, etc.) as long as it's fixed.
 *
 * Note: In our Nespresso e-commerce, this will always be the main header
 * as we don't have fixed side-bar elements. If multiple fixed elements exist
 * at first pixel, this returns the topmost one (which should be the header).
 *
 * Note: (0,0) doesn't work in Firefox, so we use (1,1) to ensure we get
 * the actual top-left corner of the viewport.
 *
 * @returns {Element|null} The fixed header element if found, otherwise null
 */
export function getFixedHeader(): Element | null {
  if (import.meta.env.SSR) {
    return null;
  }

  const header = findFixedElementByPosition(1, 1);

  if (!header) {
    return null;
  }

  // Check just below the header to see if there's another fixed element there, in case that
  // the above one is not the header, like a sticky notification bar.
  const headerBelow = findFixedElementByPosition(
    0,
    header.getBoundingClientRect().height + 1
  );

  return headerBelow ?? header;
}

function findFixedElementByPosition(x: number, y: number): Element | null {
  return (
    Array.from(document.elementsFromPoint(x, y) ?? []).find(
      isValidFixedHeaderElement
    ) ?? null
  );
}

/**
 * Retrieves the root DOM element for rendering portals (modals, etc)
 *
 * This function searches for the optimal root element in the following priority order:
 * 1. Checks predefined selectors (ROOT_SELECTORS_BY_PRIORITY)
 * 2. Falls back to document.body if no suitable element is found
 *
 * Note: Most markets typically use <main id="main"> as their root container,
 * but this provides a flexible fallback for different implementations.
 *
 * Important: If a modal is already open when this is computed, that modal becomes
 * the root element as it represents a separate DOM tree (portal) that should
 * contain its own children rather than attaching to the main document root.
 */
export function getRootElement(): Element | null {
  if (import.meta.env.SSR) {
    return null;
  }

  for (const selector of ROOT_SELECTORS_BY_PRIORITY) {
    const el = document.querySelector(selector);
    if (el) return el;
  }
  return document.body;
}

/**
 * Comprehensive validation to determine if an element is the actual fixed header.
 */
function isValidFixedHeaderElement(element: Element): boolean {
  const isFixed = window.getComputedStyle(element)?.position === 'fixed';

  if (!isFixed || element?.tagName === 'DIALOG') {
    return false;
  }

  const isFromCatalogContent = element?.getAttribute(
    DATA_ATTRIBUTE_STICKY_CONTENT
  );

  if (isFromCatalogContent) return false;

  // Note: This helps distinguish actual layout headers from other sticky elements that
  // might occupy the full viewport height (like error notifications or overlays).
  return element.getBoundingClientRect().height < window.innerHeight;
}
